import { ConditionFilterType } from '~/constants/automationRuleForm';
import { OperatorString } from '~/constants/operators';

type JSONAutomationRule = {
  description?: string;
  filter_group: JSONFilterGroup;
  name: string;
  object_id?: string;
  parcel_template_object_id: string;
  rule_type: string;
  service_level_token: string;
  account_object_id: string;
  sender_address_id?: number;
};

type JSONFilterGroup = {
  filter_criteria: JSONFilterCriteria[];
};

type JSONFilterClause = {
  operator: string;
  filter_criteria: JSONFilterCriteria[];
};

type JSONFilter = {
  field: string;
  comparator: string;
  value: JSONFilterValue;
};

type JSONFilterCriteria = (JSONFilterClause | JSONFilter)[];

type JSONFilterValue = {
  enum: string;
  value: string;
};

type BaseFormCondition = {
  filterGroup: ConditionFilterType;
  operator: OperatorString;
};

type WeightFormCondition = BaseFormCondition & {
  units: string;
  weight: number;
};

type ItemNameFormCondition = BaseFormCondition & {
  productName: string;
};

type SkuFormCondition = BaseFormCondition & {
  sku: string;
};

type OrderItemQuantityFormCondition = BaseFormCondition & {
  orderItemQuantity: number;
};

type ShippingDestinationFormCondition = BaseFormCondition & {
  shippingDestination: string;
  states?: string[];
  zipcodes?: string;
  shippingRuleType?: string;
  country?: string;
  countries?: string[];
};

type DefaultFormCondition = {
  filterGroup: ConditionFilterType;
};

type EmptyFormConditions = {
  [ConditionFilterType.DEFAULT]: DefaultFormCondition;
  [ConditionFilterType.WEIGHT]: WeightFormCondition;
  [ConditionFilterType.PRODUCT_NAME]: ItemNameFormCondition;
  [ConditionFilterType.ITEM_SKU]: SkuFormCondition;
  [ConditionFilterType.ORDER_ITEM_QUANTITY]: OrderItemQuantityFormCondition;
  [ConditionFilterType.SHIPPING_DESTINATION]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_STATES]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_ZIPCODES]: ShippingDestinationFormCondition;
  [ConditionFilterType.SHIPPING_COUNTRIES]: ShippingDestinationFormCondition;
};

// TODO: correct types for rule
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toJSON(rule: any): Maybe<JSONAutomationRule> {
  if (!rule) return null;

  const {
    description,
    filterGroup,
    name,
    objectId,
    parcelTemplateId,
    ruleType,
    serviceLevelToken,
    accountObjectId,
    senderAddressId,
  } = rule;

  const formattedRule = {
    name,
    description: description || null, // empty strings should be sent as null
    parcel_template_object_id: parcelTemplateId,
    object_id: objectId || null,
    rule_type: ruleType,
    filter_group: filterGroupToJSON(filterGroup),
    service_level_token: serviceLevelToken || null,
    account_object_id: accountObjectId || null,
    sender_address_id: senderAddressId || null,
  };

  return formattedRule;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterGroupToJSON(filterGroup: any): JSONFilterGroup {
  const { filterCriteria } = filterGroup;
  return {
    filter_criteria: filterCriteriaToJSON(filterCriteria),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterCriteriaToJSON(filterCriteria: any): JSONFilterCriteria[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return filterCriteria.map((filterCriterion: any) => {
    if (filterCriterion.operator) {
      return filterClauseToJSON(filterCriterion);
    } else {
      return filterCriterion as JSONFilter;
    }
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterClauseToJSON(filterClause: any): JSONFilterClause {
  const { operator, filterCriteria } = filterClause;
  return {
    operator,
    filter_criteria: filterCriteriaToJSON(filterCriteria),
  };
}

export { toJSON, JSONAutomationRule, EmptyFormConditions, ShippingDestinationFormCondition };
